<template>
    <node-view-wrapper
        as="div"
        :data-justify="justify"
        :style="{ display: 'flex', justifyContent: justify, transition: '.2s' }"
    >
        <div
            v-click-outside="hideResize"
            @mousedown="isResizing = true"
            class="image-template"
            :style="getTemplateStyle()"
            draggable="true"
            data-drag-handle
            :class="{ load: loading, resizing: isResizing }"
        >
            <SpinLoader v-if="loading" />

            <div v-click-outside="hide" class="">
                <transition name="fade-up">
                    <div v-if="isOpen" class="settings-modal">
                        <div class="mb-5">
                            <default-title
                                :size="14"
                                :weight="600"
                                :line-height="25"
                                text-color="#323030"
                                font-family="Inter, sans-serif"
                            >
                                {{ t("builder.image_settings") }}
                            </default-title>
                        </div>

                        <default-title
                            :size="13"
                            :weight="400"
                            :line-height="25"
                            text-color="#52565C"
                            font-family="Inter, sans-serif"
                        >
                            {{ t("builder.direction") }}:
                            <b class="ml-2" style="color: #323030">{{ t(`builder.${aligns[justify]}`) }}</b>
                        </default-title>

                        <div class="align-container mb-10">
                            <button
                                @click="justify = 'flex-start'"
                                :class="{ active: justify === 'flex-start' }"
                                class="editor-button editor-button_fill"
                            >
                                <AlignLeftIcon />
                            </button>
                            <button
                                @click="justify = 'center'"
                                :class="{ active: justify === 'center' }"
                                class="editor-button editor-button_fill"
                            >
                                <AlignCenterIcon />
                            </button>
                            <button
                                @click="justify = 'flex-end'"
                                :class="{ active: justify === 'flex-end' }"
                                class="editor-button editor-button_fill"
                            >
                                <AlignRightIcon />
                            </button>
                            <button
                                @click="justify = 'inline'"
                                :class="{ active: justify === 'inline' }"
                                class="editor-button editor-button_fill"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                                    <path
                                        d="M15 18H16.5C17.8807 18 19 16.8807 19 15.5C19 14.1193 17.8807 13 16.5 13H3V11H16.5C18.9853 11 21 13.0147 21 15.5C21 17.9853 18.9853 20 16.5 20H15V22L11 19L15 16V18ZM3 4H21V6H3V4ZM9 18V20H3V18H9Z"
                                    ></path>
                                </svg>
                            </button>
                        </div>

                        <default-title
                            :size="13"
                            :weight="400"
                            :line-height="25"
                            text-color="#52565C"
                            font-family="Inter, sans-serif"
                        >
                            {{ t("builder.visibility_image") }}
                        </default-title>

                        <div class="align-container mb-10">
                            <button
                                @click="hidedesktop = !hidedesktop"
                                :class="hidedesktop ? 'editor-button_inactive' : 'editor-button_active'"
                                class="editor-button editor-button_fill"
                            >
                                <DesktopIcon />
                            </button>
                            <button
                                @click="hidetablet = !hidetablet"
                                :class="hidetablet ? 'editor-button_inactive' : 'editor-button_active'"
                                class="editor-button editor-button_fill"
                            >
                                <TabletIcon />
                            </button>
                            <button
                                @click="hidemobile = !hidemobile"
                                :class="hidemobile ? 'editor-button_inactive' : 'editor-button_active'"
                                class="editor-button editor-button_fill"
                            >
                                <SmartphoneIcon />
                            </button>
                        </div>

                        <flex-container class="mb-10" align="center">
                            <default-title
                                :size="13"
                                :weight="400"
                                :line-height="25"
                                text-color="#52565C"
                                font-family="Inter, sans-serif"
                            >
                                {{ t("builder.to_stretch") }}
                            </default-title>
                            <CustomSwitcher @update="updateStretch()" :value="width === '100%'" class="ml-8" />
                        </flex-container>

                        <flex-container class="mb-10" align="center">
                            <default-title
                                :size="13"
                                :weight="400"
                                :line-height="25"
                                text-color="#52565C"
                                font-family="Inter, sans-serif"
                            >
                                {{ t("builder.allow_zoom") }}
                            </default-title>
                            <CustomSwitcher @update="updateZoom()" :value="allowzoom" class="ml-8" />
                        </flex-container>

                        <flex-container align="center">
                            <default-title
                                :size="13"
                                :weight="400"
                                :line-height="25"
                                text-color="#52565C"
                                font-family="Inter, sans-serif"
                            >
                                {{ t("create-course.allow_download") }}
                            </default-title>
                            <CustomSwitcher @update="updateDownload()" :value="allowdownload" class="ml-8" />
                        </flex-container>
                    </div>
                </transition>
                <transition name="fade">
                    <button @click="open()" v-if="isResizing" class="settings-handler">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M2.97813 17.0017C2.54588 16.253 2.22144 15.4717 2 14.6756C2.97247 14.1794 3.63839 13.1683 3.63839 12.0017C3.63839 10.836 2.97347 9.82546 2.0022 9.32887C2.44892 7.71797 3.29724 6.21773 4.50341 4.99147C5.41937 5.58562 6.62802 5.6568 7.63839 5.07346C8.64876 4.49012 9.19144 3.40781 9.13487 2.3175C10.8 1.88606 12.5234 1.9015 14.1418 2.32008C14.0862 3.40952 14.6288 4.49058 15.6384 5.07346C16.6488 5.65678 17.8574 5.58562 18.7733 4.99153C19.352 5.58132 19.8664 6.25297 20.2987 7.00167C20.7309 7.75036 21.0554 8.53171 21.2768 9.32774C20.3043 9.82393 19.6384 10.8351 19.6384 12.0017C19.6384 13.1675 20.3033 14.1779 21.2746 14.6745C20.8279 16.2854 19.9796 17.7856 18.7734 19.0119C17.8574 18.4178 16.6488 18.3466 15.6384 18.9299C14.628 19.5132 14.0854 20.5956 14.1419 21.6859C12.4769 22.1173 10.7535 22.1019 9.135 21.6833C9.19058 20.5938 8.64796 19.5128 7.63839 18.9299C6.62805 18.3466 5.41946 18.4177 4.50351 19.0118C3.92484 18.422 3.41039 17.7504 2.97813 17.0017ZM8.63839 17.1979C9.72977 17.828 10.5136 18.8242 10.889 19.9674C11.3873 20.0148 11.8889 20.0155 12.3873 19.969C12.7625 18.8252 13.5465 17.8283 14.6384 17.1979C15.7303 16.5674 16.9857 16.387 18.1639 16.6339C18.4533 16.2255 18.7035 15.7907 18.9116 15.3355C18.1093 14.4388 17.6384 13.2619 17.6384 12.0017C17.6384 10.7415 18.1093 9.56466 18.9116 8.66796C18.8075 8.44196 18.6925 8.21968 18.5666 8.00167C18.4407 7.78365 18.3057 7.57294 18.162 7.36988C16.9844 7.61622 15.7298 7.4356 14.6384 6.80551C13.547 6.17541 12.7633 5.17914 12.3878 4.03601C11.8896 3.9886 11.3879 3.98792 10.8896 4.03438C10.5143 5.17817 9.73027 6.1751 8.63839 6.80551C7.54647 7.43593 6.29112 7.6164 5.11295 7.36949C4.82352 7.77787 4.5733 8.21263 4.36523 8.66783C5.16748 9.56457 5.63839 10.7415 5.63839 12.0017C5.63839 13.2619 5.1675 14.4387 4.36523 15.3354C4.46934 15.5614 4.58431 15.7837 4.71019 16.0017C4.83606 16.2197 4.97107 16.4304 5.11479 16.6335C6.29244 16.3872 7.54705 16.5678 8.63839 17.1979ZM11.6384 15.0017C9.98157 15.0017 8.63839 13.6586 8.63839 12.0017C8.63839 10.3449 9.98157 9.00167 11.6384 9.00167C13.2953 9.00167 14.6384 10.3449 14.6384 12.0017C14.6384 13.6586 13.2953 15.0017 11.6384 15.0017ZM11.6384 13.0017C12.1907 13.0017 12.6384 12.554 12.6384 12.0017C12.6384 11.4494 12.1907 11.0017 11.6384 11.0017C11.0861 11.0017 10.6384 11.4494 10.6384 12.0017C10.6384 12.554 11.0861 13.0017 11.6384 13.0017Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </transition>
            </div>

            <transition name="fade">
                <div v-if="isResizing && width > 99" class="image-sizes">
                    {{ getImageSizes() }}
                </div>
            </transition>

            <div :key="index" v-for="(handler, index) in handlers">
                <transition name="scale">
                    <span
                        v-if="isResizing"
                        @mousedown.stop.prevent="handleMouseDown"
                        :data-handler="handler"
                        ref="handler"
                        class="handler"
                    ></span>
                </transition>
            </div>

            <img
                :width="width"
                :height="height"
                v-bind="node.attrs"
                :data-drag-handle="isDraggable"
                ref="resizableImg"
                alt=""
            />
        </div>
    </node-view-wrapper>
</template>

<script>
import { updatePersonalSettings } from "@common-store/personal-settings"
import FlexContainer from "@components/Containers/FlexContainer.vue"
import CustomSwitcher from "@components/Forms/CustomSwitcher.vue"
import DefaultTitle from "@components/Typography/DefaultTitle.vue"
import DesktopIcon from "@icons/DesktopIcon.vue"
import SmartphoneIcon from "@icons/SmartphoneIcon.vue"
import TabletIcon from "@icons/TabletIcon.vue"
import IsOpen from "@mixins/is-open"
import { nodeViewProps, NodeViewWrapper } from "@tiptap/vue-2"
import AlignLeftIcon from "@components/Editor/icons/AlignLeftIcon.vue"
import AlignCenterIcon from "@components/Editor/icons/AlignCenterIcon.vue"
import AlignRightIcon from "@components/Editor/icons/AlignRightIcon.vue"
import SpinLoader from "@components/Loaders/SpinLoader.vue"
import axios from "@libs/axios"
import ClickOutside from "vue-click-outside"
import { handleError } from "@helpers"

export default {
    components: {
        FlexContainer,
        CustomSwitcher,
        SmartphoneIcon,
        TabletIcon,
        DesktopIcon,
        DefaultTitle,
        SpinLoader,
        AlignRightIcon,
        AlignCenterIcon,
        AlignLeftIcon,
        NodeViewWrapper
    },
    props: nodeViewProps,
    mixins: [IsOpen],
    directives: {
        ClickOutside
    },
    data() {
        return {
            isResizing: false,
            aspectRatio: 0,
            width: "100%",
            maxWidth: "100%",
            handler: "",
            height: "auto",
            loading: false,
            lastMovement: {
                x: 0,
                y: 0
            },
            hidemobile: false,
            hidetablet: false,
            hidedesktop: false,
            allowzoom: false,
            allowdownload: false,
            justify: "flex-start",
            aligns: {
                "flex-start": "left",
                center: "center",
                inline: "inline",
                "flex-end": "right"
            },
            handlers: ["tl", "tr", "bl", "br"]
        }
    },
    computed: {
        isDraggable() {
            return this.node?.attrs?.isDraggable
        },
        resizableImg() {
            return this.$refs?.resizableImg || this.$el?.querySelector("img")
        }
    },
    mounted() {
        this.justify = this.node.attrs.justify
        this.hidemobile = this.node.attrs.hidemobile
        this.allowzoom = this.node.attrs.allowzoom
        this.allowdownload = this.node.attrs.allowdownload
        this.hidetablet = this.node.attrs.hidetablet
        this.hidedesktop = this.node.attrs.hidedesktop

        this.width = this.node.attrs.width
        this.height = this.node.attrs.height

        this.resizableImg?.addEventListener("load", this.imageLoaded, true)

        this.editor.commands.setNodeSelection(this.getPos())

        const resizeObserver = new ResizeObserver(() => {
            this.setMaxWidth()
        })

        resizeObserver.observe(this.$el)
    },
    methods: {
        getTemplateStyle() {
            const style = {}

            if (this.width === "100%") {
                style.width = "100%"
            }

            return style
        },
        updateZoom() {
            this.allowzoom = !this.allowzoom

            updatePersonalSettings("allowzoom", this.allowzoom)
        },
        updateDownload() {
            this.allowdownload = !this.allowdownload

            updatePersonalSettings("allowdownload", this.allowdownload)
        },
        updateStretch() {
            if (this.width === "100%") {
                this.width = this.maxWidth
            } else {
                this.width = "100%"
            }
            this.update()
        },
        getImageSizes() {
            return `${this.width === "100%" ? "100%" : parseInt(this.width)} × ${this.height}`
        },
        setMaxWidth() {
            this.maxWidth = this.$el.closest(".editor-container")?.offsetWidth || "100%"
        },
        imageLoaded(withCheck = true) {
            this.setMaxWidth()

            // if (this.node?.attrs?.width && this.node?.attrs?.width !== "100%") {
            //     //this.width = this.node?.attrs?.width
            // } else if (!this.node?.attrs?.width) {
            //     // this.width = "100%"
            //     // this.width = this.resizableImg?.width
            // }

            const src = this.resizableImg?.src

            if (!this.width) {
                if (this.resizableImg?.width + 50 > this.maxWidth || this.maxWidth > window.innerWidth) {
                    this.width = "100%"
                } else {
                    this.width = this.resizableImg?.width
                }
            }

            this.update()

            this.resizableImg?.removeEventListener("load", this.imageLoaded, true)

            if (
                (src.includes("base64") ||
                    (!src.includes("kwiga") && !src.includes("ahacdn") && window?.kwg_settings?.can_upload_image)) &&
                withCheck
            ) {
                this.uploadImage()
            }

            this.$forceUpdate()
            //this.height = this.resizableImg.height
            //this.aspectRatio = this.resizableImg.naturalWidth / this.resizableImg.naturalHeight
        },
        handleMouseMove(ev, isGrow = false) {
            const movement = Math.sqrt(
                Math.pow(this.lastMovement.x - ev.clientX, 2) + Math.pow(ev.clientY - this.lastMovement.y, 2)
            )

            let isNegative = ev.clientX <= this.lastMovement.x || isGrow ? 1 : -1

            if (this.handler === "tl" || this.handler === "bl") {
                isNegative *= -1
            }

            if (this.width === "100%") {
                this.imageLoaded(false)
            }

            const result = this.width - movement * isNegative

            if (result <= this.maxWidth && result > 50) {
                this.width -= movement * isNegative
            }
            this.update()
            this.setLastMovement(ev.clientX, ev.clientY)
        },

        handleMouseDown(ev) {
            this.setLastMovement(ev.clientX, ev.clientY)

            this.handler = ev.target.dataset.handler

            if (this.node?.attrs?.width && this.node?.attrs?.width !== "100%") {
                this.width = this.node?.attrs?.width
            } else {
                this.width = this.resizableImg?.width
            }

            document.addEventListener("mousemove", this.handleMouseMove)
            document.addEventListener("mouseup", this.handleMouseUp, { once: true })
        },
        handleMouseUp() {
            document.removeEventListener("mousemove", this.handleMouseMove)
            this.update()
        },
        setLastMovement(x, y) {
            this.lastMovement = {
                x,
                y
            }
        },
        hideResize() {
            this.isResizing = false
        },
        update() {
            const pos = this.getPos()
            this.updateAttributes({
                src: this.resizableImg?.src,
                width: this.width,
                height: "auto",
                justify: this.justify,
                hidemobile: this.hidemobile,
                hidetablet: this.hidetablet,
                hidedesktop: this.hidedesktop,
                allowzoom: this.allowzoom,
                allowdownload: this.allowdownload,
                "data-file-id": this.resizableImg?.dataset?.fileId
            })
            this.editor.commands.setNodeSelection(pos)
        },
        setSrc(src, id) {
            if (this.resizableImg) {
                this.resizableImg.src = src
                this.resizableImg.dataset.fileId = id
            }

            this.update()
        },
        getImageMimeType(blob) {
            // Extract MIME type from data URL
            const mimeMatch = this.resizableImg?.src?.match(/data:([^;]+);/)
            // Note: When copying an image through clipboard, the browser converts it to PNG,
            // so blob.type will be 'image/png' even if the original was JPEG
            return mimeMatch ? mimeMatch[1] : blob.type || "image/png"
        },
        getFileExtensionFromMime(mimeType) {
            // Determine file extension from MIME type
            const mimeToExt = {
                "image/jpeg": "jpg",
                "image/jpg": "jpg",
                "image/png": "png",
                "image/gif": "gif",
                "image/webp": "webp",
                "image/svg+xml": "svg",
                "image/bmp": "bmp"
            }
            return mimeToExt[mimeType] || mimeType.split("/")[1] || "png"
        },
        generateFileName(ext) {
            // Try to extract file name from alt or title attribute
            const originalName = this.resizableImg?.alt || this.resizableImg?.title || ""

            if (originalName) {
                // Remove existing extension and add the correct one
                return `${originalName.replace(/\.[^/.]+$/, "")}.${ext}`
            }

            // Generate unique name with timestamp
            return `image_${Date.now()}.${ext}`
        },
        uploadImage() {
            fetch(this.resizableImg?.src)
                .then(res => res.blob())
                .then(async blob => {
                    const formData = new FormData()

                    const mimeType = this.getImageMimeType(blob)
                    const ext = this.getFileExtensionFromMime(mimeType)
                    const fileName = this.generateFileName(ext)

                    formData.append("file", blob, fileName)

                    if (window.current_cabinet_id) {
                        formData.append("cabinet_id", window.current_cabinet_id)
                    }

                    try {
                        this.loading = true
                        const response = await axios.post(
                            window.file_storage_url + "?model=infoUnitContent",
                            formData,
                            {
                                headers: { "content-type": blob.type }
                            }
                        )

                        const { uuid } = response.data.data

                        let checkStatus = setInterval(() => {
                            axios.get(`/files/${uuid}`).then(response => {
                                let data = response.data.data
                                if (
                                    data.process_status === "completed" &&
                                    data.url &&
                                    !data.url.includes("fileservice")
                                ) {
                                    clearInterval(checkStatus)
                                    this.setSrc(data.url, data.id)
                                    this.loading = false
                                }
                            })
                        }, 2000)
                    } catch (error) {
                        console.error(error)
                        this.loading = false
                        handleError(error)
                    }
                })
        }
    },
    watch: {
        justify() {
            this.update()
        },
        hidedesktop() {
            this.update()
        },
        hidemobile() {
            this.update()
        },
        allowzoom() {
            this.update()
        },
        allowdownload() {
            this.update()
        },
        hidetablet() {
            this.update()
        }
    }
}
</script>
<style lang="sass">
@import "../style/button"
$color: #2473f0
.drag-handle
    flex: 0 0 auto
    position: absolute
    z-index: 5
    background-color: #fff
    top: 0.3rem
    margin-right: 0.5rem
    cursor: grab
    .drag-handle-icon
        width: 1rem
        height: 1rem
        background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 16"><path fill-opacity="0.2" d="M4 14c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM2 6C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6C.9 0 0 .9 0 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>')
        background-repeat: no-repeat
        background-size: contain
        background-position: center
.settings-modal
    position: absolute
    top: 8px
    right: 8px
    z-index: 10
    background-color: #fff
    border-radius: 8px
    min-width: 180px
    box-shadow:  0 4px 10px rgba(128, 158, 191, 0.15)
    padding: 15px 10px 20px
.editor-container
    .image-template
        font-style: normal
        border: 2px solid transparent
        position: relative
        cursor: pointer
        margin: 5px 0
        display: flex
        img
            margin: 0 !important
        &.load
            img
                opacity: .4
        .spin-loader
            position: absolute
            top: 50%
            left: 50%
            transform: translate(-50%, -50%)
            z-index: 5
    .image-sizes
        position: absolute
        bottom: 0
        right: 0
        z-index: 5
        background: rgba(#000000, .8)
        border-top-left-radius: 20px
        color: #fff
        padding: 6px 12px 4px 15px
        font-size: 13px
        line-height: 21px
        font-family: Inter, sans-serif
    .settings-handler
        position: absolute
        top: 0
        right: 0
        z-index: 5
        background: rgba(#000000, .8)
        box-shadow:  0 4px 10px rgba(128, 158, 191, 0.15)
        border-bottom-left-radius: 20px
        color: #fff
        padding: 6px 9px 6px 15px
        font-size: 13px
        line-height: 21px
        font-family: Inter, sans-serif
        transition: .2s
        &:hover
            background: rgba(#000000, .9)
            padding: 6px 10px 7px 17px
    .align-container
        border-radius: 4px
        display: none
        width: max-content
        height: auto
        background: #fff
        left: 50%
        gap: 0.45rem
        padding: 4px
        box-shadow:  0 4px 10px rgba(128, 158, 191, 0.15)
    .align-container
        display: flex
    .image-template.resizing
        cursor: default
        border-color: $color
        border-radius: 5px
    .handler
        display: block
        width: 10px
        height: 10px
        border-radius: 2px
        background: $color
        position: absolute
        z-index: 6
        &[data-handler="tl"]
            top: -5px
            left: -5px
            cursor: nwse-resize
        &[data-handler="tr"]
            top: -5px
            right: -5px
            cursor: nesw-resize
        &[data-handler="bl"]
            bottom: -5px
            left: -5px
            cursor: nesw-resize
        &[data-handler="br"]
            bottom: -5px
            right: -5px
            cursor: nwse-resize
</style>
